import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import API, { getMediaUrl } from "../services/api";

function isRedJodi(value) {
  return RED_CHART_JODIS.has(String(value || "").padStart(2, "0"));
}

const RED_CHART_JODIS = new Set([
  "00", "11", "22", "33", "44", "55", "66", "77", "88", "99",
  "05", "16", "27", "38", "49", "50", "61", "62", "72", "83", "94",
]);

function parseChartDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getWeekStart(dateString) {
  const current = parseChartDate(dateString);
  if (!current) return null;
  const day = current.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  current.setDate(current.getDate() + mondayOffset);
  return current;
}

function formatWeekRange(date) {
  const start = new Date(date.getTime());
  const end = new Date(date.getTime());
  end.setDate(start.getDate() + 6);
  const year = start.getFullYear();
  const startLabel = `${String(start.getDate()).padStart(2, "0")}-${start.toLocaleString("en-IN", { month: "short" })}`;
  const endLabel = `${String(end.getDate()).padStart(2, "0")}-${end.toLocaleString("en-IN", { month: "short" })}`;
  return { year, label: `${startLabel} to ${endLabel}` };
}

function ChartsMarketDetailPage() {
  const { marketId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view") === "panel" ? "panel" : "jodi";
  const [payload, setPayload] = useState({ market: null, rows: [] });
  const [loading, setLoading] = useState(true);
  const [visibleWeekCount, setVisibleWeekCount] = useState(120);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setLoading(true);
    setVisibleWeekCount(120);
    API.get(`/charts/market/${marketId}`)
      .then((res) => setPayload(res.data || { market: null, rows: [] }))
      .catch(() => setPayload({ market: null, rows: [] }))
      .finally(() => setLoading(false));
  }, [marketId]);

  const isCompact = viewportWidth <= 520;
  const mobileFit = isCompact;
  const gridTemplateColumns = mobileFit
    ? "58px repeat(7, minmax(0, 1fr))"
    : "120px repeat(7, 1fr)";
  const gridMinWidth = mobileFit ? "100%" : 820;

  const uploadedFile = useMemo(() => {
    const files = Array.isArray(payload.files) ? payload.files : [];
    return files.find((item) => String(item.chart_type || "").toLowerCase() === view) || null;
  }, [payload.files, view]);

  const weeks = useMemo(() => {
    const grouped = new Map();
    (payload.rows || []).forEach((row) => {
      const weekStart = getWeekStart(row.chart_date);
      if (!weekStart) return;
      const weekKey = weekStart.toISOString().slice(0, 10);
      if (!grouped.has(weekKey)) {
        const range = formatWeekRange(weekStart);
        grouped.set(weekKey, {
          key: weekKey,
          year: range.year,
          range: range.label,
          days: Array.from({ length: 7 }, () => null),
        });
      }
      const current = grouped.get(weekKey);
      const rowDate = parseChartDate(row.chart_date);
      if (!rowDate) return;
      const dayIndex = (rowDate.getDay() + 6) % 7;
      current.days[dayIndex] = row;
    });

    return Array.from(grouped.values()).sort((left, right) => (left.key < right.key ? 1 : -1));
  }, [payload.rows]);

  const visibleWeeks = useMemo(() => weeks.slice(0, visibleWeekCount), [weeks, visibleWeekCount]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/charts");
  };

  return (
    <div style={page}>
      <PublicNav />
      <div style={subHeader}>
        <button type="button" style={backButton} onClick={handleBack}><ArrowLeft size={20} /></button>
        <h1 style={title}>{payload.market?.name || "Chart"}</h1>
        <button type="button" style={modeButton}>{view === "panel" ? "PANEL" : "JODI"}</button>
      </div>

      <main style={main}>
        {uploadedFile ? (
          <section style={pdfShell}>
            <div style={pdfHeader}>
              <strong>{uploadedFile.original_name || `${view.toUpperCase()} PDF Chart`}</strong>
              <a style={pdfLink} href={getMediaUrl(uploadedFile.file_url)} target="_blank" rel="noreferrer">
                Open PDF
              </a>
            </div>
            <iframe
              title={`${payload.market?.name || "Market"} ${view} PDF chart`}
              src={getMediaUrl(uploadedFile.file_url)}
              style={pdfFrame}
            />
          </section>
        ) : null}

        {loading ? <div style={loadingState}>Loading chart...</div> : null}

        {!loading && weeks.length ? (
          <div style={tableShell}>
            <div style={{ ...tableInner, overflowX: mobileFit ? "hidden" : "auto" }}>
              <div style={{ ...headerRow, minWidth: gridMinWidth, gridTemplateColumns }}>
                <div style={{ ...dateHead, fontSize: isCompact ? 13 : 16, padding: isCompact ? "12px 6px" : "16px 10px" }}>Date</div>
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                  <div key={day} style={{ ...dayHead, fontSize: isCompact ? 10 : 16, padding: isCompact ? "10px 1px" : "16px 6px" }}>{day}</div>
                ))}
              </div>

              {visibleWeeks.map((week) => (
                <div key={week.key} style={{ ...weekRow, minWidth: gridMinWidth, gridTemplateColumns }}>
                  <div style={{ ...dateCol, padding: isCompact ? "12px 6px" : "16px 10px" }}>
                    <strong style={{ ...yearLabel, fontSize: isCompact ? 13 : 15 }}>{week.year}</strong>
                    <span style={{ ...rangeLabel, fontSize: isCompact ? 11 : 14 }}>{week.range}</span>
                  </div>

                  {week.days.map((row, index) => {
                    if (!row) {
                      return (
                      <div key={`${week.key}-${index}`} style={{ ...cell, minHeight: isCompact ? 64 : 98, padding: isCompact ? "6px 1px" : "10px 4px" }}>
                          <span style={{ ...missingMark, fontSize: isCompact ? 16 : 22 }}>-</span>
                        </div>
                      );
                    }

                    const jodi = `${row.open_digit || "*"}${row.close_digit || "*"}`;
                    const redJodi = isRedJodi(jodi);

                    return (
                      <div key={`${week.key}-${row.chart_date}`} style={{ ...(redJodi ? redBracketCell : cell), minHeight: isCompact ? 64 : 98, padding: isCompact ? "6px 1px" : "10px 4px", gap: isCompact ? 1 : 3 }}>
                        {view === "panel" ? (
                          <>
                            <span style={{ ...(redJodi ? redText : valueText), fontSize: isCompact ? 9 : 18 }}>{row.open_pana || "***"}</span>
                            <span style={{ ...(redJodi ? redJodiText : jodiText), fontSize: isCompact ? 11 : 20 }}>{jodi}</span>
                            <span style={{ ...(redJodi ? redText : valueText), fontSize: isCompact ? 9 : 18 }}>{row.close_pana || "***"}</span>
                          </>
                        ) : (
                          <span style={{ ...(redJodi ? redJodiText : blueJodiText), fontSize: isCompact ? 12 : 20 }}>{jodi}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {!loading && visibleWeeks.length < weeks.length ? (
          <button type="button" style={loadMoreButton} onClick={() => setVisibleWeekCount((count) => count + 120)}>
            Load older chart
          </button>
        ) : null}
        {!loading && !weeks.length && !uploadedFile ? <div style={emptyState}>No chart uploaded yet for this market.</div> : null}
      </main>
    </div>
  );
}

export default ChartsMarketDetailPage;

const page = { minHeight: "100vh", background: "#f8fafc", fontFamily: "Inter, system-ui, sans-serif", color: "#0f172a" };
const subHeader = { height: 52, background: "#fff", borderBottom: "1px solid #e5e7eb", display: "grid", gridTemplateColumns: "44px 1fr auto", alignItems: "center", gap: 12, padding: "0 12px" };
const backButton = { width: 40, height: 40, border: 0, borderRadius: 14, background: "#eff6ff", color: "#2563eb", display: "grid", placeItems: "center" };
const title = { margin: 0, fontSize: "clamp(18px, 6vw, 22px)", fontWeight: 900 };
const modeButton = { minWidth: 92, height: 44, border: 0, borderRadius: 14, background: "linear-gradient(90deg,#9333ea,#a855f7)", color: "#fff", fontWeight: 900, fontSize: 16 };
const main = { padding: 10 };
const pdfShell = { marginBottom: 12, background: "#fff", borderRadius: 20, border: "1px solid #dbeafe", overflow: "hidden", boxShadow: "0 12px 28px rgba(37,99,235,.08)" };
const pdfHeader = { minHeight: 52, padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, borderBottom: "1px solid #e5e7eb", color: "#0f172a" };
const pdfLink = { flex: "0 0 auto", color: "#fff", background: "#2563eb", borderRadius: 12, padding: "9px 12px", fontWeight: 900, textDecoration: "none", fontSize: 13 };
const pdfFrame = { display: "block", width: "100%", height: "min(72vh, 720px)", border: 0, background: "#f8fafc" };
const tableShell = { background: "#fff", borderRadius: 20, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 12px 28px rgba(15,23,42,.05)" };
const tableInner = { overflowX: "auto" };
const headerRow = { minWidth: 820, display: "grid", gridTemplateColumns: "120px repeat(7, 1fr)", borderBottom: "1px solid #e5e7eb", background: "#fff" };
const dateHead = { padding: "16px 10px", fontWeight: 900, fontSize: 16 };
const dayHead = { padding: "16px 6px", textAlign: "center", fontWeight: 900, fontSize: 16 };
const weekRow = { minWidth: 820, display: "grid", gridTemplateColumns: "120px repeat(7, 1fr)", borderBottom: "1px solid #eef2f7", background: "#fff" };
const dateCol = { padding: "16px 10px", display: "grid", alignContent: "center", gap: 6, background: "#f8fbff" };
const yearLabel = { fontSize: 15, color: "#334155" };
const rangeLabel = { fontSize: 14, lineHeight: 1.35, color: "#64748b" };
const cell = { minHeight: 98, borderLeft: "1px solid #f1f5f9", display: "grid", alignContent: "center", justifyItems: "center", gap: 3, padding: "10px 4px" };
const redBracketCell = { ...cell, background: "linear-gradient(180deg,#fff1f2,#ffe4e6)", borderLeft: "1px solid #fecdd3", boxShadow: "inset 0 0 0 1px rgba(220,38,38,.12)" };
const valueText = { fontSize: 18, fontWeight: 800, color: "#111827", lineHeight: 1.05 };
const redText = { ...valueText, color: "#dc2626" };
const jodiText = { fontSize: 20, fontWeight: 900, color: "#111827", lineHeight: 1 };
const redJodiText = { ...jodiText, color: "#dc2626" };
const blueJodiText = { ...jodiText, color: "#2563eb" };
const missingMark = { fontSize: 22, color: "#cbd5e1", fontWeight: 700 };
const emptyState = { marginTop: 12, background: "#fff", border: "1px dashed #cbd5e1", borderRadius: 18, padding: "20px 16px", textAlign: "center", color: "#64748b", fontWeight: 700 };
const loadingState = { ...emptyState, border: "1px solid #dbeafe", color: "#2563eb", background: "linear-gradient(135deg,#ffffff,#eff6ff)" };
const loadMoreButton = { width: "100%", minHeight: 48, marginTop: 12, border: 0, borderRadius: 16, background: "linear-gradient(90deg,#2563eb,#7c3aed)", color: "#fff", fontWeight: 900, fontSize: 15, boxShadow: "0 14px 24px rgba(37,99,235,.18)" };
